import { createApi } from '@reduxjs/toolkit/query/react';
import { BackendResponse } from '../../types';
import { parseQueryParams } from '../../utils';
import { baseQueryWithReauth } from './baseQuery';
import { ActivityRequestKeys } from '../../pages/ActivityTracking/constants/activity.tableConfig';

export enum TableConfigPage {
  MYLIST = 'MYLIST',
  REPORTS = 'REPORTS',
  APPROVALS = 'APPROVALS',
  ASSIGNMENT = 'ASSIGNMENT',
}

export interface ColumnConfigItem {
  label: string;
  key: ActivityRequestKeys;
  // key: string;
  order: number;
}

export interface FieldConfigDto {
  data: ColumnConfigItem[];
}

export interface CreateTableConfigDto {
  name: string;
  fieldConfigJson: FieldConfigDto;
  isDefault?: boolean;
}

export interface UpdateTableConfigDto {
  name: string;
  fieldConfigJson: FieldConfigDto;
  isDefault?: boolean;
}

export interface ApplyTableConfigDto {
  configId: number;
  page: TableConfigPage;
}

export interface TableConfig {
  id: number;
  name: string;
  fieldConfigJson: FieldConfigDto;
  isDefault: boolean;
  page?: TableConfigPage;
  createdAt?: string;
  updatedAt?: string;
  createdByUser?: {
    ID: number;
    FullName: string;
    EmailID: string;
  };
}

export interface CurrentTableConfigResponse {
  config: TableConfig;
  mappingId: number | null;
  appliedAt: string | null;
  isDefault?: boolean;
}

const activityTableConfigApi = createApi({
  reducerPath: 'activityTableConfig',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['TableConfig'],
  endpoints: builder => ({
    getTableConfigList: builder.query<
      BackendResponse<TableConfig[]>,
      { page: TableConfigPage }
    >({
      query: params => ({
        url: `/activity-table-config/config-list${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['TableConfig'],
    }),
    getTableConfig: builder.query<
      BackendResponse<TableConfig>,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/activity-table-config/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, { id }) => [{ type: 'TableConfig', id }],
    }),
    createTableConfig: builder.mutation<
      BackendResponse<TableConfig>,
      CreateTableConfigDto & { page: TableConfigPage }
    >({
      query: payload => ({
        url: '/activity-table-config/create-config',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['TableConfig'],
    }),
    updateTableConfig: builder.mutation<
      BackendResponse<TableConfig>,
      { id: number; data: UpdateTableConfigDto }
    >({
      query: ({ id, data }) => ({
        url: `/activity-table-config/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['TableConfig'],
    }),
    getCurrentTableConfig: builder.query<
      BackendResponse<CurrentTableConfigResponse>,
      { page: TableConfigPage }
    >({
      query: params => ({
        url: `/activity-table-config/current-config-page${parseQueryParams(params)}`,
        method: 'GET',
      }),
      providesTags: ['TableConfig'],
    }),
    applyTableConfig: builder.mutation<
      BackendResponse<void>,
      ApplyTableConfigDto
    >({
      query: payload => ({
        url: `/activity-table-config/apply`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['TableConfig'],
    }),
    deleteTableConfig: builder.mutation<
      BackendResponse<void>,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/activity-table-config/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['TableConfig'],
    }),
  }),
});

export const {
  useGetTableConfigListQuery,
  useGetTableConfigQuery,
  useGetCurrentTableConfigQuery,
  useCreateTableConfigMutation,
  useUpdateTableConfigMutation,
  useApplyTableConfigMutation,
  useDeleteTableConfigMutation,
} = activityTableConfigApi;

export default activityTableConfigApi;

